import { useState } from 'react';
import type { Profile, ProfileType } from '../types/vault';
import { config } from '../data/config';

interface ProfileFormProps {
  profile?: Profile | null;
  onSave: (profile: Profile) => void;
  onCancel: () => void;
}

export function ProfileForm({ profile, onSave, onCancel }: ProfileFormProps) {
  const [displayName, setDisplayName] = useState(profile?.displayName ?? '');
  const [profileType, setProfileType] = useState<ProfileType>(profile?.profileType ?? 'Individual');
  const [legalName, setLegalName] = useState(profile?.legalName ?? '');
  const [aliases, setAliases] = useState(profile?.aliases?.join(', ') ?? '');
  const [notes, setNotes] = useState(profile?.notes ?? '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!displayName.trim()) return;
    const now = new Date().toISOString();
    onSave({
      id: profile?.id ?? '',
      displayName: displayName.trim(),
      legalName: legalName.trim() || undefined,
      profileType,
      aliases: aliases.trim() ? aliases.split(',').map(a => a.trim()).filter(Boolean) : undefined,
      notes: notes.trim() || undefined,
      archived: profile?.archived ?? false,
      created: profile?.created ?? now,
      updated: now,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Display Name <span className="text-red-500">*</span></label>
        <input value={displayName} onChange={e => setDisplayName(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0F766E] focus:border-[#0F766E]" required maxLength={200} placeholder="e.g. Anish" />
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Profile Type <span className="text-red-500">*</span></label>
        <select value={profileType} onChange={e => setProfileType(e.target.value as ProfileType)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0F766E] focus:border-[#0F766E]">
          {config.profileTypes.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Legal Name</label>
        <input value={legalName} onChange={e => setLegalName(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0F766E]" maxLength={300} placeholder="e.g. BPA Solutions Pty Ltd" />
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Aliases <span className="text-slate-400 font-normal">(comma separated)</span></label>
        <input value={aliases} onChange={e => setAliases(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0F766E]" maxLength={500} placeholder="e.g. Anish M, AM" />
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
        <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={4} maxLength={config.limits.profileNotes} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0F766E] resize-y" placeholder="Additional notes..." />
        <p className="text-xs text-slate-400 text-right mt-1">{notes.length.toLocaleString()} / {config.limits.profileNotes.toLocaleString()}</p>
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F766E]">Cancel</button>
        <button type="submit" disabled={!displayName.trim()} className="px-4 py-2 text-sm bg-[#102D4C] text-white rounded-lg hover:bg-[#173B61] disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[#0F766E]">
          {profile ? 'Save Changes' : 'Create Profile'}
        </button>
      </div>
    </form>
  );
}